import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../../lib/scroll";
import { trackCta } from "../../lib/track";

const LINKS = [
  { id: "why-move", label: "Why Gen 2", testId: "nav-link-why-gen2" },
  { id: "milestones", label: "Milestones", testId: "nav-link-milestones" },
  { id: "matrix", label: "Matrix", testId: "nav-link-matrix" },
  { id: "migration-plan", label: "Migration Plan", testId: "nav-link-migration-plan" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[box-shadow] duration-500 border-b bg-white border-slate-200 ${
        scrolled || menuOpen ? "shadow-[0_8px_30px_rgba(15,23,42,0.08)]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-brand-logo"
          onClick={() => go("hero")}
          className="flex items-center group"
        >
          <img src="/hp-logo.png" alt="HP" className="h-10 w-auto mix-blend-multiply" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={link.testId}
              onClick={() => go(link.id)}
              className="relative text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-[#024AD8] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {link.label}
            </button>
          ))}
          <a
            href="/fit-check"
            data-testid="nav-link-fit-check"
            className="relative text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-[#024AD8] after:transition-[width] after:duration-300 hover:after:w-full"
          >
            Fit Check
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-contact-button"
            onClick={() => { trackCta("nav"); go("contact"); }}
            className="group relative hidden sm:inline-flex items-center gap-2 bg-black text-white text-sm font-semibold px-5 py-2.5 rounded-full overflow-hidden transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            <span className="absolute inset-0 bg-[#024AD8] translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out rounded-full" />
            <span className="relative">Connect with your HP Poly specialist</span>
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-slate-100 bg-white"
          >
            <div className="px-6 py-4 flex flex-col">
              {LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`nav-mobile-${link.id}`}
                  onClick={() => go(link.id)}
                  className="text-left py-3.5 text-sm font-semibold text-slate-700 border-b border-slate-100 last:border-0 hover:text-[#024AD8] transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <a href="/fit-check" className="py-3.5 text-sm font-semibold text-slate-700 border-b border-slate-100 hover:text-[#024AD8] transition-colors">
                Fit Check
              </a>
              <button
                data-testid="nav-mobile-cta-contact-button"
                onClick={() => { trackCta("nav-mobile"); go("contact"); }}
                className="mt-3 mb-1 inline-flex items-center justify-center bg-[#024AD8] text-white text-sm font-semibold px-5 py-3 rounded-full"
              >
                Connect with your HP Poly specialist
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
